<template>
  <div
    v-if="entries.length > 0 || showCart"
    class="entriesCart"
  >
    <div
      class="entriesCart__head"
      @click="showCart = !showCart"
    >
      <span
        v-if="!showCart"
        class="uk-margin-left uk-button uk-button-primary"
      >Continue to checkout</span>
    </div>

    <Modal
      v-if="showCart"
      @close-modal="showCart = !showCart"
    >
      <template #header>
        <h1>Checkout</h1>
      </template>
      <template #default>
        <p v-if="isDefaultState && event.meta.checkout.text_unpaid_items_cart_header">
          {{ event.meta.checkout.text_unpaid_items_cart_header }}
        </p>

        <div
          v-if="!completed && !waitingForPayment"
          class="entriesCart__summary"
        >
          <div
            class="cartItems"
          >
            <div
              v-if="entries.length === 0"
              class="uk-alert uk-alert-warning"
            >
              <p>There are no unpaid entries.</p>
            </div>
            <Item
              v-for="entry of entries"
              :key="entry.data.reference"
              :entry="entry"
            />

            <div
              v-if="entries.length > 0"
              class="cartItems__total"
            >
              Entries total: ${{ cartEntriesTotal }}
            </div>
          </div>
          <div
            v-if="entries.length > 0 && !completed && event.data.allow_donations"
            class="cartItems"
          >
            <Donation />

            <div
              v-if="donation"
              class="cartItems__total"
            >
              Total to pay including donation: ${{ cartTotal }}
            </div>
          </div>
        </div>

        <div v-if="event.meta.checkout.requirements && !completed">
          <p
            v-for="(req, reqI) in event.meta.checkout.requirements"
            :key="req.label"
          >
            <label><input
              :ref="'checkout_requirement_' + reqI"
              :name="'checkout_requirement_' + reqI"
              type="checkbox"
              value="1"
              @input="changeRequired"
            > {{ req.label }} </label>
          </p>
        </div>

        <div
          v-if="waitingForPayment"
          class="uk-alert uk-alert-info"
        >
          <span class="uk-icon-spin uk-icon-spinner" /> Your payment is processing, please wait while it is confirmed.
        </div>

        <div style="position: relative;">
          <div
            v-if="!requiredValid"
            style="position: absolute;z-index:1000;top:0;right:0;bottom:0;left:0;background-color: rgba(255,255,255,0.7)"
          />
          <div
            v-show="showPaymentOptions"
            class="uk-width-1-1 uk-width-medium-8-10 uk-container-center"
          >
            <div
              id="paypal-button-container"
              ref="paypalContainer"
            />
          </div>

          <div
            v-if="isFreeCheckout && entries.length > 0"
            class="uk-width-1-1 uk-width-medium-8-10 uk-container-center"
          >
            <a
              class="uk-button uk-button-primary uk-width-1-1"
              @click="submitFreeOrder"
            >Free Checkout</a>
          </div>
        </div>
        <div
          v-if="completed"
          class="uk-alert uk-alert-success"
        >
          Thank you! Your payment was successful and your entries have been validated.
        </div>

        <div
          v-if="cancelled"
          class="uk-alert uk-alert-info"
        >
          Your order has been cancelled and you have not been debited.
        </div>
        <div
          v-if="failed"
          class="uk-alert uk-alert-danger"
        >
          An error has occurred while processing this transaction. <strong>Do not attempt to pay again as you may be billed twice.</strong>
          Please contact support for assistance before trying to pay again.
        </div>
      </template>
    </modal>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import EventEntryModel from "../../models/EventEntryModel";
import CartItemModel, {
  CART_ITEM_DONATION,
} from "../../models/CartItemModel";
import DivisionModel from "../../models/DivisionModel";
import EventModel from "../../models/EventModel";
import Modal from '../misc/Modal.vue';
import Item from "./cart/Item.vue";
import OrderModel from "../../models/OrderModel";
import Donation from "./cart/Donation.vue";
import { captureException, captureMessage } from "../../lib/error_handler";

declare var paypal: any;

interface PayPalItem {
  name: string;
  sku: string;
  description?: string;
  quantity: number;
  unit_amount: { value: number, currency_code: string };
}

@Component({
  components: {
    Modal,
    Item,
    Donation,
  }
})
export default class EntriesCart extends Vue {
  @Prop() entries!: EventEntryModel[];
  @Prop() event!: EventModel;

  order: any;
  completed = false;
  cancelled = false;
  failed = false;
  waitingForPayment = false;
  requiredChecked = 0;

  data(): any {
    return {
      order: null
    }
  }

  get requiredValid(): boolean {
    return this.event.meta.checkout.requirements.length <= this.requiredChecked;
  }

  changeRequired(ev: InputEvent): void {
    if ((ev.target as HTMLInputElement).checked) {
      this.requiredChecked += 1;
    } else {
      if (this.requiredChecked > 0) {
        this.requiredChecked -= 1;
      }
    }
  }

  get isDefaultState(): boolean {
    return !this.completed && !this.cancelled && !this.failed && !this.waitingForPayment;
  }

  get cartEntriesTotal(): number {
    const items = this.$store.getters['events/cart/allItems'];

    let total = 0;
    for (const i of items) {
      if (!i.isDonation()) {
        total += i.data.value;
      }
    }

    return total;
  }

  get showCart(): boolean {
    return this.$store.getters['events/cart/showCart'];
  }

  set showCart(v: boolean) {
    if (!v) {
      this.resetPaymentState();
      this.destroyPayPalButtons();
    }
    this.$store.dispatch('events/cart/showCart', v);
  }

  get showPaymentOptions(): boolean {
    return !this.completed && !this.failed && !this.waitingForPayment && this.cartTotal > 0;
  }

  get isFreeCheckout(): boolean {
    return this.cartTotal === 0 && this.isDefaultState;
  }

  get paypalItems(): PayPalItem[] {
    const items: PayPalItem[] = [];
    if (!this.entries || this.entries.length === 0) {
      return items;
    }

    const event: EventModel = this.$store.getters['events/byId'](this.entries[0].data.event_id);

    for (const entry of this.entries) {
      const division: DivisionModel = this.$store.getters['events/divisions/getById'](entry.data.division_id);
      const cartItem: CartItemModel = this.$store.getters['events/cart/bySKU'](entry.data.SKU);

      if (!division || !cartItem) {
        return []; // not loaded yet
      }

      items.push({
        name: `${event.data.title}: ${entry.getFullName()} – ${division.data.title}`,
        sku: entry.data.SKU,
        quantity: cartItem.data.quantity,
        unit_amount: { value: cartItem.data.value, currency_code: 'USD' },
      });
    }

    const donation = this.donation;
    if (donation) {
      items.push({
        name: 'Donation',
        sku: donation.data.sku,
        quantity: donation.data.quantity,
        unit_amount: { value: donation.data.value, currency_code: 'USD' }
      });
    }

    return items;
  }

  get donation(): CartItemModel | null {
    return this.$store.getters['events/cart/bySKU'](CART_ITEM_DONATION);
  }

  get cartTotal(): number {
    return this.paypalItems.reduce((a: number, b: PayPalItem) => a + b.unit_amount.value, 0);
  }

  get cartTotalFormatted(): string
  {
    return '$' + this.cartTotal;
  }

  get cartItems(): CartItemModel[] {
    return this.$store.getters['events/cart/allItems'];
  }

  resetPaymentState(): void {
    this.failed = false;
    this.completed = false;
    this.cancelled = false;
    this.waitingForPayment = false;
  }

  updateEntriesStatus(order: OrderModel): Promise<void> {
    return new Promise((resolve) => {
      for (const cartItem of order.items) {
        if (!cartItem.isDonation()) {
          const entry: EventEntryModel = this.$store.getters['events/getEntryBySKU'](cartItem.data.sku);
          this.$store.dispatch('events/syncEntryFromServer', entry.data.reference);
        }
      }

      resolve();
    });
  }

  submitFreeOrder(): void {
    this.waitingForPayment = true;
    this.$store.dispatch('events/cart/submitFreeOrder', {items: this.cartItems}).then((order: OrderModel) => {
      this.updateEntriesStatus(order).then(() => {
        this.$store.dispatch('events/cart/clearCart');
        this.resetPaymentState();
        this.completed = true;
      });
    }).catch(() => {
      this.resetPaymentState();
      this.failed = true;
    });
  }

  destroyPayPalButtons(): void {
    if (this.$refs.paypalContainer) {
      this.order = null;
      (this.$refs.paypalContainer as HTMLElement).innerHTML = '';
    }
  }

  createPayPalButtons(): void {
    if (this.order) {
      return;
    }

    try {
      this.order = paypal.Buttons({
        createOrder: (data: any, actions: any) => {
          this.resetPaymentState();
          return actions.order.create({
            application_context: {
              shipping_preference: 'NO_SHIPPING'
            },
            purchase_units: [{
              items: this.paypalItems,
              amount: {
                value: this.cartTotal,
                breakdown: {
                  item_total: {
                    currency_code: 'USD',
                    value: this.cartTotal,
                  },
                }
              }
            }]
          });
        },
        onApprove: (data: any, actions: any) => {
          this.waitingForPayment = true;
          return actions.order.capture().then((details: any) => {
            this.$store.dispatch('events/cart/verifyOrder', {orderId: details.id, clientResponse: JSON.stringify(details)}).then((order: OrderModel) => {
              this.updateEntriesStatus(order).then(() => {
                this.$store.dispatch('events/cart/clearCart');
                this.resetPaymentState();
                this.completed = true;
              });
            }).catch((e) => {
              this.resetPaymentState();
              this.failed = true;
              captureException(e);
            });
          });
        },
        onError: (err: any) => {
          this.resetPaymentState();
          this.failed = true;
          captureMessage('PayPal onError()', err);
        },
        onCancel: (data: any, actions: any) => {
          this.resetPaymentState();
          this.cancelled = true;
          captureMessage('PayPal onCancel()', data);
        }
      }).render('#paypal-button-container');
    } catch (e) {
      captureException(e);
    }
  }

  @Watch('showCart', {immediate: true})
  togglePayPalButtonsOnShow(v: boolean): void {
    this.requiredChecked = 0;
    if (v && !this.isFreeCheckout) {
      setTimeout(this.createPayPalButtons, 0);
    } else {
      this.destroyPayPalButtons();
    }
  }

  @Watch('isFreeCheckout', {immediate: true})
  togglePayPalButtonsOnFree(v: boolean): void {
    if (!v && this.showCart) {
      setTimeout(this.createPayPalButtons, 0);
    } else {
      this.destroyPayPalButtons();
    }
  }

}
</script>

<style>
.entriesCart {
  margin-top: 1em;
}

.entriesCart__head, .entriesCart__summary {
  padding: 1em;
}

.entriesCart__head {
  font-weight: bold;
  font-size: 1.2em;
  text-align: right;
}

.cartItems__total {
  text-align: right;
  font-weight: bold;
  margin: 1em 0;
  padding: 1em;
  border: 1px solid #f1f1f1;
}
</style>
